import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import type { Announcement } from "~/services/announcementService";
import { getAnnouncements } from "~/services/announcementService";
import { useBranch } from "~/contexts/BranchContext";

const AnnouncementCarousel = () => {
  const { i18n } = useTranslation();
  const { branch } = useBranch();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!branch?.id) return;

      const response = await getAnnouncements({
        branch_id: branch.id,
        is_active: true,
        disable_pagination: true,
      });

      if (response.success && response.data && response.data.length > 0) {
        setAnnouncements(response.data);
      }
    };

    fetchAnnouncements();
  }, [branch?.id]);

  // Auto-advance carousel on mobile
  useEffect(() => {
    if (!isMobile || announcements.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isMobile, announcements.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const renderAnnouncementCard = (announcement: Announcement) => {
    const isArabic = i18n.language === "ar";
    const { media_type, media_url, content, type, ar } = announcement;

    // Check if Arabic content actually has values (not just empty object)
    const hasArabicContent = ar?.content && (ar.content.title || ar.content.description || ar.content.discount);
    const hasArabicMedia = ar?.media_url;

    // Use Arabic content if available and language is Arabic, otherwise fallback to English
    const displayContent = isArabic && hasArabicContent ? ar.content : content;
    const displayMediaType = isArabic && hasArabicMedia && ar?.media_type ? ar.media_type : media_type;
    const displayMediaUrl = isArabic && hasArabicMedia ? ar.media_url : media_url;
    const displayType = isArabic && ar?.type ? ar.type : type;

    const hasMedia = (displayMediaType === "image" || displayMediaType === "video") && displayMediaUrl;
    const hasText = displayContent?.title || displayContent?.description || displayContent?.discount;

    return (
      <div className={`flex justify-center items-center bg-white rounded-xl shadow-lg   ${isArabic ? "rtl" : "ltr"}`}>
        {/* Type Badge */}
        <div className={` absolute top-4 z-10 ${isArabic ? "right-4" : "left-4"}`}>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
              displayType === "sale"
                ? "bg-red-100 text-red-700"
                : displayType === "gift"
                  ? "bg-green-100 text-green-700"
                  : displayType === "event"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700"
            }`}
          >
            {displayType}
          </span>
        </div>

        {/* Content */}
        <div className={`flex ${hasMedia && hasText ? "flex-col md:flex-row" : "flex-col"} items-center `}>
          {/* Media Section */}
          {displayMediaType === "image" && displayMediaUrl && (
            <div className={`${hasText ? "w-full md:w-1/2 flex-shrink-0" : "w-full flex justify-center"}`}>
              <img
                src={displayMediaUrl}
                alt={displayContent?.title || "Announcement"}
                className={`${hasText ? "w-full h-auto object-contain" : "h-auto object-contain max-h-[400px]"}`}
              />
            </div>
          )}

          {displayMediaType === "video" && displayMediaUrl && (
            <div className={`${hasText ? "w-full md:w-1/2 flex-shrink-0" : "w-full flex justify-center"}`}>
              <video
                src={displayMediaUrl}
                controls
                autoPlay
                muted
                loop
                className={`${hasText ? "w-full h-auto object-contain" : "h-auto object-contain max-h-[400px]"}`}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {/* Text Content */}
          {hasText && (
            <div
              className={`flex-1 p-6 md:p-8 flex flex-col justify-center items-center text-center w-full`}
            >
              {displayContent?.title && (
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                  {displayContent.title}
                </h3>
              )}
              
              {displayContent?.discount && (
                <p className="text-3xl md:text-4xl font-extrabold text-red-600 mb-3">
                  {displayContent.discount}
                </p>
              )}
             
              {displayContent?.description && (
                <p className="text-gray-600 text-base md:text-lg whitespace-pre-line">
                  {displayContent.description}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (announcements.length === 0) return null;

  return (
    <div className="w-full py-6">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="relative">
        {/* Desktop: All announcements in a slider */}
        {!isMobile && (
          <div className="hidden md:block">
            {announcements.length > 1 && (
              <>
                {/* Navigation Buttons */}
                <button
                  onClick={goToPrev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Previous announcement"
                >
                  <IoChevronBack className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Next announcement"
                >
                  <IoChevronForward className="w-6 h-6 text-gray-700" />
                </button>
              </>
            )}

            <div className="overflow-hidden">
              <motion.div
                className="flex"
                animate={{ x: `-${currentIndex * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="min-w-full px-2">
                    {renderAnnouncementCard(announcement)}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Dots Indicator */}
            {announcements.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {announcements.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? "bg-gray-800 w-6"
                        : "bg-gray-300"
                    }`}
                    aria-label={`Go to announcement ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mobile: Auto-carousel */}
        {isMobile && (
          <div className="md:hidden">
            <div className="overflow-hidden rounded-xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderAnnouncementCard(announcements[currentIndex])}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dots Indicator */}
            {announcements.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {announcements.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? "bg-gray-800 w-6"
                        : "bg-gray-300"
                    }`}
                    aria-label={`Go to announcement ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AnnouncementCarousel;
